import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { queryKeys } from '@/modules/api/queryKeys';
import { useUpdateDemoNegotiationMutation } from '@/modules/demoNegotiation/hooks/useUpdateDemoNegotiationMutation';
import { DemoNegotiationDialog } from '@/pages/AnalysisAndOfferPage/tabs/DemoNegotiations/DemoNegotiationDialog/DemoNegotiationDialog';
import { noop } from '@/utils/noop';

import { periodNotSelectedState } from '../../../NegotiationsDnD/constants';
import {
  NegotiationsDnD,
  UpdatePeriodIndexParams,
} from '../../../NegotiationsDnD/NegotiationsDnD';
import { DnDNegotiation } from '../../../types';

import { useGetDemoNegotiationsQuery } from './hooks/useGetDemoNegotiationsQuery';

export const DemoNegotiationsDnD = () => {
  // We need local copy of negotations,
  // because we need to mutate it later on drag events
  const [items, setItems] = useState<DnDNegotiation[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [demoNegotiationData, setDemoNegotiationData] =
    useState<DnDNegotiation | null>(null);
  const queryClient = useQueryClient();

  const {
    data: negotiations,
    isPending,
    isRefetching,
  } = useGetDemoNegotiationsQuery();
  const updateDemoNegotiationMutation = useUpdateDemoNegotiationMutation();

  const updatePeriodIndex = async ({
    negotiationId,
    destinationIndex,
    originIndex,
  }: UpdatePeriodIndexParams) => {
    try {
      await updateDemoNegotiationMutation.mutateAsync({
        negotiationId,
        periodIndex: destinationIndex,
      });
      if (
        periodNotSelectedState.some((state) =>
          [destinationIndex, originIndex].includes(state)
        )
      ) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.customer.getDemoMetrics],
        });
      }
    } catch (error) {
      noop();
    }
  };

  useEffect(() => {
    if (isPending) return;
    setItems(negotiations || []);
  }, [negotiations, isPending]);

  const handleNegotiationCardClick = (negotiation: DnDNegotiation) => {
    setDemoNegotiationData(negotiation);
    setIsOpen(true);
  };

  return (
    <>
      <NegotiationsDnD
        items={items}
        updatePeriodIndex={updatePeriodIndex}
        setItems={setItems}
        isRefetching={isRefetching}
        isLoading={isPending}
        onNegotiationCardClick={handleNegotiationCardClick}
      />

      {demoNegotiationData && (
        <DemoNegotiationDialog
          negotiationId={demoNegotiationData.id}
          estimatedDiscount={
            demoNegotiationData?.metrics?.estimatedDiscount || 0
          }
          estimatedInflation={
            demoNegotiationData?.metrics?.estimatedInflation || 0
          }
          termRoles={demoNegotiationData.termRoles}
          demoNegotiationPayload={{
            suppliersCount: demoNegotiationData?.metrics?.suppliersCount || 0,
            title: demoNegotiationData.title,
            totalSpend: demoNegotiationData.totalSpend || 0,
          }}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
